import React, { useEffect, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import styled from "styled-components";

import {
  RevenueSkeletonChart,
  RevenueSkeletonChartGrid,
  RevenueSkeletonChartLine,
  RevenueSkeletonLegend,
  RevenueSkeletonLegendItem,
  RevenueSkeletonTitle,
} from "../../DashBoardNew/dashBoardNewStyles";

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px;
  gap: 12px;
  width: 100%;
  height: 100%;
  max-height: 261px;
  background: #ffffff;
  border: 1px solid #d3e0fc;
  border-radius: 8px;
  box-sizing: border-box;
  min-width: 0;

  @media (max-width: 1024px) {
    min-height: 240px;
  }

  @media (max-width: 768px) {
    min-height: 280px;
    padding: 12px;
    gap: 10px;
  }

  @media (max-width: 480px) {
    min-height: 250px;
    padding: 10px;
    gap: 8px;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 8px;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const TitleText = styled.h3`
  margin: 0;
  font-weight: 700;
  font-size: 14px;
  color: #06100f;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 13px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const LegendContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0px;
  gap: 4px;
  flex: 0 0 auto;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 3px;
  }

  @media (max-width: 480px) {
    flex: 1 1 auto;
  }
`;

const LegendItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 10px 8px;
  gap: 4px;
  width: 57px;
  height: 20px;
  background: ${({ color }) => color};
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  color: ${({ textColor }) => textColor};

  @media (max-width: 768px) {
    padding: 8px 6px;
    font-size: 11px;
    width: auto;
    height: auto;
  }

  @media (max-width: 480px) {
    padding: 6px 4px;
    font-size: 10px;
  }
`;

const LegendDot = styled.div`
  width: 8px;
  height: 8px;
  background: ${({ color }) => color};
  border-radius: 50%;
`;

const LegendText = styled.span`
  font-weight: 600;
`;

const ChartContainer = styled.div`
  width: 100%;
  height: 196px;
  position: relative;
  flex: 1;

  @media (max-width: 1024px) {
    height: 180px;
  }

  @media (max-width: 768px) {
    height: 160px;
  }

  @media (max-width: 480px) {
    height: 140px;
  }
`;

const CompleteJobCompareChart = ({
  getDashboardCompleteJobCompare,
  completeJobCompareIsLoading,
  selectedOrganization,
  lastYearCompleteJobCompare,
  thisYearCompleteJobCompare,
  currentMonthIndex: propCurrentMonthIndex,
}) => {
  const chartRef = useRef(null);
  const [comparisonData, setComparisonData] = useState({
    lastYear: null,
    thisYear: null,
    currentMonthIndex: null,
  });

  useEffect(() => {
    if (selectedOrganization?.id && getDashboardCompleteJobCompare) {
      getDashboardCompleteJobCompare({
        organization_id: selectedOrganization.id,
      });
    }
  }, [selectedOrganization, getDashboardCompleteJobCompare]);

  useEffect(() => {
    if (lastYearCompleteJobCompare || thisYearCompleteJobCompare) {
      setComparisonData({
        lastYear: lastYearCompleteJobCompare,
        thisYear: thisYearCompleteJobCompare,
        currentMonthIndex:
          propCurrentMonthIndex !== null && propCurrentMonthIndex !== undefined
            ? propCurrentMonthIndex
            : null,
      });
    }
  }, [
    lastYearCompleteJobCompare,
    thisYearCompleteJobCompare,
    propCurrentMonthIndex,
  ]);

  const currentMonthIndex =
    comparisonData.currentMonthIndex !== null &&
    comparisonData.currentMonthIndex !== undefined
      ? comparisonData.currentMonthIndex
      : new Date().getMonth();

  const formatNumber = value => {
    const numericValue = Number(value) || 0;
    return new Intl.NumberFormat("en-US").format(numericValue);
  };

  const gradientPlugin = {
    id: "gradientPlugin",
    afterDatasetsDraw: chart => {
      const { ctx, chartArea } = chart;
      const yScale = chart.scales.y;

      if (!yScale) return;

      const greenGradient = ctx.createLinearGradient(
        0,
        chartArea.top,
        0,
        chartArea.bottom,
      );
      greenGradient.addColorStop(0.0426, "rgba(232, 249, 239, 0.95)");
      greenGradient.addColorStop(0.9679, "rgba(232, 249, 239, 0)");

      const blueGradient = ctx.createLinearGradient(
        0,
        chartArea.top,
        0,
        chartArea.bottom,
      );
      blueGradient.addColorStop(0.3898, "rgba(34, 102, 239, 0.12)");
      blueGradient.addColorStop(1, "rgba(34, 102, 239, 0)");

      chart.data.datasets[0].backgroundColor = greenGradient;
      chart.data.datasets[1].backgroundColor = blueGradient;
    },
  };

  const thisYearData = comparisonData.thisYear?.data || [];
  const lastYearData = comparisonData.lastYear?.data || [];
  const thisYearLabel =
    comparisonData.thisYear?.year || new Date().getFullYear();
  const lastYearLabel =
    comparisonData.lastYear?.year || new Date().getFullYear() - 1;

  const padArray = (arr, length) => {
    const padded = [...arr];
    while (padded.length < length) {
      padded.push(0);
    }
    return padded.slice(0, length);
  };

  const paddedThisYearData = padArray(thisYearData, 12);
  const paddedLastYearData = padArray(lastYearData, 12);

  const chartData = {
    labels: [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ],
    datasets: [
      {
        label: String(thisYearLabel),
        data: paddedThisYearData.slice(0, currentMonthIndex + 1),
        borderColor: "#16A34A",
        borderWidth: 2,
        pointRadius: ctx => (ctx.dataIndex === currentMonthIndex ? 1 : 4),
        pointBackgroundColor: "#16A34A",
        pointBorderColor: "#FFFFFF",
        pointBorderWidth: 2,
        pointHitRadius: ctx => (ctx.dataIndex === currentMonthIndex ? 15 : 5),
        tension: 0.4,
        fill: true,
      },
      {
        label: String(lastYearLabel),
        data: paddedLastYearData,
        borderColor: "#2266EF",
        borderWidth: 2,
        pointRadius: ctx => (ctx.dataIndex === currentMonthIndex ? 1 : 4),
        pointBackgroundColor: "#2266EF",
        pointBorderColor: "#FFFFFF",
        pointBorderWidth: 2,
        pointHitRadius: ctx => (ctx.dataIndex === currentMonthIndex ? 15 : 5),
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    backgroundColor: "transparent",
    interaction: {
      intersect: true,
      mode: "nearest",
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        backgroundColor: "#FFFFFF",
        titleColor: "#06100F",
        bodyColor: "#06100F",
        borderColor: "#EFF4F3",
        borderWidth: 1,
        padding: 4,
        borderRadius: 8,
        bodyFont: {
          size: 12,
          weight: 600,
        },
        boxShadow: "0px 2px 20px rgba(28, 79, 74, 0.15)",
        displayColors: false,
        yAlign: "bottom",
        xAlign: "center",
        callbacks: {
          title: function () {
            return "";
          },
          label: function (context) {
            return formatNumber(context.parsed.y);
          },
          labelTextColor: function (context) {
            return context.dataset.borderColor;
          },
          afterLabel: function () {
            return "";
          },
        },
      },
      verticalLine: {
        id: "verticalLine",
        afterDraw: chart => {
          const { ctx, chartArea } = chart;
          const xScale = chart.scales.x;
          const yScale = chart.scales.y;

          if (!xScale || !yScale) return;

          const currentMonthPixelX = xScale.getPixelForValue(currentMonthIndex);

          ctx.save();
          ctx.strokeStyle = "#16A34A";
          ctx.lineWidth = 1;
          ctx.setLineDash([4, 4]);
          ctx.beginPath();
          ctx.moveTo(currentMonthPixelX, chartArea.top);
          ctx.lineTo(currentMonthPixelX, chartArea.bottom);
          ctx.stroke();
          ctx.restore();
        },
      },
      pointerIcons: {
        id: "pointerIcons",
        afterDraw: chart => {
          const { ctx } = chart;
          const xScale = chart.scales.x;
          const yScale = chart.scales.y;

          if (!xScale || !yScale) return;

          chart.data.datasets.forEach((dataset, datasetIndex) => {
            const data = dataset.data;
            const color = dataset.borderColor;

            if (datasetIndex === 0 || datasetIndex === 1) {
              const currentValue = data[currentMonthIndex];
              const xPixel = xScale.getPixelForValue(currentMonthIndex);
              const yPixel = yScale.getPixelForValue(currentValue);

              ctx.save();
              ctx.strokeStyle = color;
              ctx.lineWidth = 1.5;
              ctx.beginPath();
              ctx.arc(xPixel, yPixel, 6, 0, 2 * Math.PI);
              ctx.stroke();

              ctx.fillStyle = color;
              ctx.beginPath();
              ctx.arc(xPixel, yPixel, 3, 0, 2 * Math.PI);
              ctx.fill();

              ctx.restore();
            }
          });
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: true,
          color: "#DBE6E480",
          drawBorder: false,
        },
        ticks: {
          color: "#3C4D4B",
          font: {
            size: 12,
            weight: 400,
            family: "Mona Sans",
          },
          padding: 0,
        },
        stacked: false,
      },
      y: {
        beginAtZero: true,
        grid: {
          display: true,
          color: "#DBE6E480",
          drawBorder: false,
        },
        ticks: {
          color: "#7B8281",
          font: {
            size: 12,
            weight: 400,
            family: "Mona Sans",
          },
          callback: function (value) {
            return formatNumber(value);
          },
          padding: 0,
        },
        stacked: false,
      },
    },
  };

  const maxValue = Math.max(...paddedThisYearData, ...paddedLastYearData, 10);
  const yAxisMax = Math.ceil(maxValue / 10) * 10;

  const updatedChartOptions = {
    ...chartOptions,
    scales: {
      ...chartOptions.scales,
      y: {
        ...chartOptions.scales.y,
        max: yAxisMax,
      },
    },
  };

  if (completeJobCompareIsLoading) {
    return (
      <CardContainer>
        <HeaderContainer>
          <RevenueSkeletonTitle />
          <RevenueSkeletonLegend>
            <RevenueSkeletonLegendItem />
            <RevenueSkeletonLegendItem />
          </RevenueSkeletonLegend>
        </HeaderContainer>
        <RevenueSkeletonChart>
          <RevenueSkeletonChartGrid>
            <RevenueSkeletonChartLine
              top="0%"
              left="0%"
              width="100%"
              height="100%"
            />
          </RevenueSkeletonChartGrid>
        </RevenueSkeletonChart>
      </CardContainer>
    );
  }

  return (
    <CardContainer>
      <HeaderContainer>
        <TitleText>
          Task Comparison {thisYearLabel} vs {lastYearLabel}
        </TitleText>
        <LegendContainer>
          <LegendItem color="#E8F9EF" textColor="#16A34A">
            <LegendDot color="#16A34A" />
            <LegendText>{thisYearLabel}</LegendText>
          </LegendItem>
          <LegendItem color="#EDF8FE" textColor="#2266EF">
            <LegendDot color="#2266EF" />
            <LegendText>{lastYearLabel}</LegendText>
          </LegendItem>
        </LegendContainer>
      </HeaderContainer>
      <ChartContainer>
        <Line
          ref={chartRef}
          data={chartData}
          options={updatedChartOptions}
          plugins={[
            gradientPlugin,
            updatedChartOptions.plugins.verticalLine,
            updatedChartOptions.plugins.pointerIcons,
          ]}
        />
      </ChartContainer>
    </CardContainer>
  );
};

export default CompleteJobCompareChart;
